import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { Lock } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { relativeFromIso } from '@/utils/format';
import { ALL_STUDIOS } from '@/data/personas';
import {
  buildGapList,
  buildQueue,
  buildDaySchedule,
  type GapBucket,
  type GapListRow,
  type GapWeekWindow,
  type QueueItem,
} from '@/domain/queueEngine';
import type { StudioId } from '@/domain/types';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';
import QueueWorkModal, { type GapTarget } from '@/components/queue/QueueWorkModal';

type MessageChannel = 'sms' | 'meta-dm' | 'ig-dm' | 'email';

// The five-rung fill ladder (board 08), worked left to right. Rung 5 is dimmed:
// forfeits & no-shows are the last resort. The recently-tattooed flag shows on
// the deposit + open-business rungs only.
const RUNGS: Array<{ key: GapBucket; label: string; dim?: boolean; flag?: boolean }> = [
  { key: 'bring-forward', label: 'Bring forward' },
  { key: 'deposit', label: 'Deposit on file', flag: true },
  { key: 'past-client', label: 'Open business', flag: true },
  { key: 'expired', label: 'Expired' },
  { key: 'no-show', label: 'No-shows', dim: true },
];

/** ISO for a decimal hour on a given day (local), mirroring QueueWorkModal. */
function hourToIso(dayIso: string, hour: number): string {
  const d = new Date(dayIso);
  d.setHours(Math.floor(hour), Math.round((hour - Math.floor(hour)) * 60), 0, 0);
  return d.toISOString();
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The Sweeper's one screen: fill this week's gaps, one artist at a time (bd
 * tv-ch5, board 08). Left: an artist rail (this week, one artist at a time, with
 * a gap count). Main: the selected artist's gaps as a horizontal gap-chip row,
 * then the selected gap's candidates as a five-rung ladder shown as tabs, worked
 * left to right. Money (deposit-on-file) and open business are rungs 2 + 3 — no
 * separate cohort list. All artists equal; the sweeper confirms on the call.
 *
 * Defaults to this week across every studio (the Sweeper screen's use). The
 * agent's "Next week gaps" surface reuses the same board with weekWindow="next"
 * and its own scoped studios (bd tv-570).
 */
interface SweeperWorkspaceProps {
  weekWindow?: GapWeekWindow;
  studioIds?: StudioId[];
}

export default function SweeperWorkspace({
  weekWindow = 'current',
  studioIds = ALL_STUDIOS,
}: SweeperWorkspaceProps = {}) {
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const pieces = useBrainStore((s) => s.pieces);
  const aiConversations = useBrainStore((s) => s.aiConversations);
  const artists = useBrainStore((s) => s.artists);
  const rebookFollowupTasks = useBrainStore((s) => s.rebookFollowupTasks);
  const studios = useBrainStore((s) => s.studios);
  const sequenceState = useBrainStore((s) => s.sequenceState);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const [selectedArtistId, setSelectedArtistId] = useState<string | null>(null);
  const [selectedGapId, setSelectedGapId] = useState<string | null>(null);
  // The rung override is keyed to the gap it was chosen for, so switching gaps
  // cleanly falls back to that gap's first non-empty rung (no reset effect).
  const [rungPick, setRungPick] = useState<{ gapId: string; rung: GapBucket } | null>(null);
  // tv-dme: how the NON-preferred bring-forward candidates are ordered. Prefer-
  // brought-forward clients are always pinned first automatically (engine sort);
  // this only reorders the remainder. Default matches the engine (furthest-out
  // first — the most lead time to backfill).
  const [bfSort, setBfSort] = useState<'furthest' | 'soonest'>('furthest');
  const [modalItem, setModalItem] = useState<QueueItem | null>(null);
  const [modalChannel, setModalChannel] = useState<MessageChannel | undefined>(undefined);

  // This week's gaps across every studio, grouped one-artist-at-a-time. Rail is
  // ordered by gap count (biggest clumps first), then name.
  const rows = useMemo(
    () => buildGapList({ artists, bookings, nowIso: clockIso, studioIds, weekWindow }),
    [artists, bookings, clockIso, studioIds, weekWindow],
  );

  const artistGroups = useMemo(() => {
    const byArtist = new Map<string, { artist: GapListRow['artist']; rows: GapListRow[] }>();
    for (const row of rows) {
      const g = byArtist.get(row.artist.id);
      if (g) g.rows.push(row);
      else byArtist.set(row.artist.id, { artist: row.artist, rows: [row] });
    }
    return [...byArtist.values()].sort(
      (a, b) => b.rows.length - a.rows.length || a.artist.displayName.localeCompare(b.artist.displayName),
    );
  }, [rows]);

  const selectedGroup =
    artistGroups.find((g) => g.artist.id === selectedArtistId) ?? artistGroups[0];

  // The selected artist's gaps: >4h/full days first (tier 1), then part days, by
  // open hours descending, then soonest.
  const artistGaps = useMemo(() => {
    if (!selectedGroup) return [];
    return [...selectedGroup.rows].sort(
      (a, b) =>
        a.tier - b.tier ||
        b.openHours - a.openHours ||
        new Date(a.dayIso).getTime() - new Date(b.dayIso).getTime(),
    );
  }, [selectedGroup]);

  const selectedGap = artistGaps.find((g) => g.id === selectedGapId) ?? artistGaps[0];

  const items = useMemo(() => {
    if (!selectedGap) return [];
    return buildQueue({
      customers,
      bookings,
      deposits,
      pieces,
      aiConversations,
      artists,
      rebookFollowupTasks,
      studioIds,
      nowIso: clockIso,
      gapFilter: { artistId: selectedGap.artist.id, dayIso: selectedGap.dayIso },
      sequenceState,
    });
  }, [
    selectedGap,
    customers,
    bookings,
    deposits,
    pieces,
    aiConversations,
    artists,
    rebookFollowupTasks,
    studioIds,
    clockIso,
    sequenceState,
  ]);

  const rungCounts = useMemo(() => {
    const counts: Record<GapBucket, number> = {
      'bring-forward': 0,
      deposit: 0,
      'past-client': 0,
      expired: 0,
      'no-show': 0,
    };
    for (const item of items) if (item.gapBucket) counts[item.gapBucket] += 1;
    return counts;
  }, [items]);

  // Each gap opens on its first non-empty rung (worked left to right); a pick
  // only applies to the gap it was made for.
  const firstNonEmptyRung = RUNGS.find((r) => rungCounts[r.key] > 0)?.key ?? 'bring-forward';
  const activeRung =
    rungPick && rungPick.gapId === selectedGap?.id ? rungPick.rung : firstNonEmptyRung;
  const rungItems = useMemo(
    () => items.filter((i) => i.gapBucket === activeRung),
    [items, activeRung],
  );

  // tv-dme: on the bring-forward rung, prefer-brought-forward candidates stay
  // pinned to the top (the engine already sorts them there); the sort control
  // only reorders the non-preferred remainder by their booking date.
  const orderedRungItems = useMemo(() => {
    if (activeRung !== 'bring-forward') return rungItems;
    const preferred = rungItems.filter((i) => i.preferBroughtForward);
    const rest = rungItems.filter((i) => !i.preferBroughtForward);
    const dateMs = (i: QueueItem) =>
      i.bringForwardBookingIso ? new Date(i.bringForwardBookingIso).getTime() : 0;
    rest.sort((a, b) => (bfSort === 'soonest' ? dateMs(a) - dateMs(b) : dateMs(b) - dateMs(a)));
    return [...preferred, ...rest];
  }, [rungItems, activeRung, bfSort]);

  // Ground the modal in the gap's largest real free window.
  const gapTarget: GapTarget | undefined = useMemo(() => {
    if (!selectedGap) return undefined;
    const schedule = buildDaySchedule({ artist: selectedGap.artist, bookings, dayIso: selectedGap.dayIso });
    const largest = [...schedule.freeWindows].sort((a, b) => b.hours - a.hours)[0];
    const startHour = largest?.startHour ?? schedule.working?.startHour ?? 10;
    const endHour = largest?.endHour ?? schedule.working?.endHour ?? 18;
    return {
      artistId: selectedGap.artist.id,
      dayIso: selectedGap.dayIso,
      startIso: hourToIso(selectedGap.dayIso, startHour),
      endIso: hourToIso(selectedGap.dayIso, endHour),
    };
  }, [selectedGap, bookings]);

  const onOpen = (item: QueueItem) => {
    setModalItem(item);
    setModalChannel(undefined);
  };

  const onSelectArtist = (id: string) => {
    setSelectedArtistId(id);
    setSelectedGapId(null);
  };

  return (
    <div data-testid="sweeper-workspace">
      {artistGroups.length === 0 ? (
        <div
          className="panel-dark p-6 text-center font-serif text-cream-soft max-w-2xl"
          data-testid="sweeper-empty"
        >
          No open gaps this week. Every rostered hour is booked.
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-8 max-w-screen-2xl">
          {/* LEFT: artist rail, one artist at a time */}
          <aside className="col-span-12 lg:col-span-3" data-testid="sweeper-artist-rail">
            <div className="mb-3">
              <p className="font-inter text-sm text-cream font-semibold">By artist</p>
              <p className="font-serif text-[12px] text-cream-faint mt-0.5">
                Clear one artist, then the next.
              </p>
            </div>
            <ul className="flex flex-col gap-1">
              {artistGroups.map((g) => {
                const active = selectedGroup?.artist.id === g.artist.id;
                return (
                  <li key={g.artist.id}>
                    <button
                      type="button"
                      onClick={() => onSelectArtist(g.artist.id)}
                      data-testid={`sweeper-artist-${g.artist.id}`}
                      className={clsx(
                        'w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 transition',
                        active ? 'bg-gold/10 border border-gold-bright/40' : 'border border-transparent hover:bg-ink-soft/60',
                      )}
                    >
                      <span
                        className={clsx(
                          'shrink-0 w-8 h-8 rounded-full grid place-items-center font-cinzel text-[12px]',
                          active ? 'bg-gold/20 text-gold-bright' : 'bg-ink-soft text-cream-soft',
                        )}
                      >
                        {initials(g.artist.displayName)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={clsx('block truncate text-sm font-semibold', active ? 'text-gold-bright' : 'text-cream')}>
                          {g.artist.displayName}
                        </span>
                        <span className="block truncate font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                          {studios[g.artist.studioId]?.name ?? g.artist.studioId}
                        </span>
                      </span>
                      <span
                        className={clsx(
                          'shrink-0 font-mono text-xs font-semibold tabular-nums',
                          active ? 'text-gold-bright' : 'text-cream-soft',
                        )}
                      >
                        {g.rows.length} {g.rows.length === 1 ? 'gap' : 'gaps'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* MAIN: selected artist -> gap chips -> rung ladder */}
          <div className="col-span-12 lg:col-span-9 lg:border-l lg:border-gold-dim/20 lg:pl-8">
            {selectedGroup ? (
              <>
                <div className="mb-4">
                  <p className="font-inter text-sm text-cream font-semibold">
                    {selectedGroup.artist.displayName} ·{' '}
                    {studios[selectedGroup.artist.studioId]?.name ?? selectedGroup.artist.studioId} · this week
                  </p>
                  <p className="font-serif text-[12px] text-cream-faint mt-0.5">
                    {artistGaps.length} gap{artistGaps.length === 1 ? '' : 's'} · &gt;4h first
                  </p>
                </div>

                {/* Gap chips */}
                <div className="flex gap-2.5 flex-wrap mb-5" data-testid="sweeper-gap-chips">
                  {artistGaps.map((gap) => {
                    const active = selectedGap?.id === gap.id;
                    const full = gap.openHours >= gap.rosteredHours;
                    return (
                      <button
                        key={gap.id}
                        type="button"
                        onClick={() => setSelectedGapId(gap.id)}
                        data-testid={`sweeper-gap-${gap.id}`}
                        className={clsx(
                          'rounded-lg border px-3 py-2 text-left transition',
                          active
                            ? 'border-gold-bright bg-gold/[0.08]'
                            : 'border-gold-dim/30 bg-ink-deep hover:border-gold-bright/50',
                        )}
                      >
                        <span className={clsx('block text-[13px] font-semibold', active ? 'text-gold-bright' : 'text-cream')}>
                          {format(new Date(gap.dayIso), 'EEE d MMM')}
                        </span>
                        <span className="block font-serif text-[11.5px] text-cream-soft mt-0.5">
                          {full ? 'Full day' : `${gap.openHours}h open`}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {selectedGap ? (
                  <>
                    <ArtistDaySchedule artistId={selectedGap.artist.id} dayIso={selectedGap.dayIso} />

                    {/* Rung ladder as tabs, worked left to right */}
                    <div className="mt-5 flex items-center gap-5 border-b border-gold-dim/25 flex-wrap" data-testid="sweeper-rungs">
                      {RUNGS.map((rung, i) => {
                        const active = activeRung === rung.key;
                        const count = rungCounts[rung.key];
                        return (
                          <button
                            key={rung.key}
                            type="button"
                            onClick={() => selectedGap && setRungPick({ gapId: selectedGap.id, rung: rung.key })}
                            data-testid={`sweeper-rung-${rung.key}`}
                            className={clsx(
                              'flex items-center gap-2 pb-2.5 -mb-px border-b-2 text-[12.5px] font-semibold transition',
                              active
                                ? 'border-gold-bright text-cream'
                                : 'border-transparent text-cream-faint hover:text-cream-soft',
                              rung.dim && !active && 'opacity-60',
                            )}
                          >
                            <span className="text-cream-faint font-mono text-[10px]">{i + 1}</span>
                            {rung.label}
                            <span
                              className={clsx(
                                'rounded-full px-1.5 py-px font-mono text-[10px] tabular-nums',
                                active ? 'bg-gold/15 text-gold-bright' : 'bg-ink-soft text-cream-faint',
                              )}
                            >
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Bring-forward sort control: prefer-brought-forward clients
                        are always pinned first (a fact, not a toggle); this only
                        orders the remaining candidates (tv-dme). */}
                    {activeRung === 'bring-forward' && orderedRungItems.length > 0 ? (
                      <div
                        className="mt-4 flex items-center gap-2"
                        data-testid="sweeper-bf-sort"
                      >
                        <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                          Then order by
                        </span>
                        {([
                          { key: 'furthest', label: 'Furthest out' },
                          { key: 'soonest', label: 'Soonest' },
                        ] as const).map((opt) => (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => setBfSort(opt.key)}
                            data-testid={`sweeper-bf-sort-${opt.key}`}
                            aria-pressed={bfSort === opt.key}
                            className={clsx(
                              'rounded-md border px-2.5 py-1 font-inter text-[11px] transition',
                              bfSort === opt.key
                                ? 'border-gold-bright text-gold-bright bg-gold/10'
                                : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/40',
                            )}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : null}

                    {/* Active rung candidates */}
                    <div className="mt-4" data-testid="sweeper-candidates">
                      {orderedRungItems.length === 0 ? (
                        <p className="font-serif text-sm text-cream-faint py-6">
                          Nothing on this rung for this gap. Try the next rung along.
                        </p>
                      ) : (
                        <ul>
                          {orderedRungItems.map((item) => (
                            <CandidateRow
                              key={item.id}
                              item={item}
                              showFlag={RUNGS.find((r) => r.key === activeRung)?.flag === true}
                              onBook={() => onOpen(item)}
                            />
                          ))}
                        </ul>
                      )}
                    </div>

                    <p className="mt-5 font-serif text-[12px] text-cream-faint" data-testid="sweeper-note">
                      Rungs worked left to right · all artists equal · Sweeper confirms on the call,
                      so filled gaps skip the confirmations cadence.
                    </p>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      )}

      <QueueWorkModal
        item={modalItem}
        initialChannel={modalChannel}
        gap={gapTarget}
        onClose={() => setModalItem(null)}
      />
    </div>
  );
}

function CandidateRow({
  item,
  showFlag,
  onBook,
}: {
  item: QueueItem;
  showFlag: boolean;
  onBook: () => void;
}) {
  const name =
    item.customer.preferredName ?? `${item.customer.firstName} ${item.customer.lastName}`;
  const flag = showFlag && item.recentlyTattooedDays !== undefined;
  return (
    <li
      className="flex items-center gap-4 border-t border-gold-dim/15 py-3.5 first:border-t-0"
      data-testid={`sweeper-cand-${item.customer.id}`}
    >
      <div className="flex-1 min-w-0">
        <p className="font-inter text-[13.5px] text-cream font-semibold">{name}</p>
        <p className="font-serif text-[12.5px] text-cream-soft mt-0.5">
          {item.detail ?? item.matchReason ?? 'Open business'}
        </p>
        {item.preferBroughtForward ? (
          <span
            data-testid={`sweeper-prefer-${item.customer.id}`}
            className="inline-block mt-1.5 rounded-full border border-gold-bright/50 bg-gold/10 px-2 py-0.5 font-inter text-[10px] uppercase tracking-widest text-gold-bright"
          >
            Brought forward on request
          </span>
        ) : null}
        {flag ? (
          <span className="inline-block mt-1.5 rounded-full border border-blood/40 px-2 py-0.5 font-inter text-[10px] uppercase tracking-widest text-blood">
            Tattooed {item.recentlyTattooedDays} {item.recentlyTattooedDays === 1 ? 'day' : 'days'} ago
          </span>
        ) : null}
        {item.dormant ? (
          <span
            data-testid={`sweeper-locked-${item.customer.id}`}
            className="inline-flex items-center gap-1.5 mt-1.5 ml-1.5 rounded-full border border-blood/30 px-2 py-0.5 font-inter text-[10px] uppercase tracking-widest text-cream-faint"
          >
            <Lock size={10} className="text-blood/70 shrink-0" />
            In a sequence · free {item.sequenceDueAtIso ? relativeFromIso(item.sequenceDueAtIso) : 'soon'}
          </span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onBook}
        className={clsx(
          'shrink-0 rounded-md px-4 py-2 text-xs font-semibold transition',
          // A locked lead (in its call-sequence cooldown) is not booked by
          // accident: a subtler, bordered override rather than the solid gold.
          item.dormant
            ? 'border border-gold-dim/40 text-cream-soft hover:border-gold-bright hover:text-cream'
            : 'bg-gold-bright text-ink-black hover:bg-gold',
        )}
        data-testid={`sweeper-book-${item.customer.id}`}
      >
        {item.dormant ? 'Book anyway' : 'Book'}
      </button>
    </li>
  );
}
